"use client";

import { useState } from "react";
import Modal from "..";
import { Controller, useForm, useWatch } from "react-hook-form";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader } from "lucide-react";

interface EnrollProps {
  children: React.ReactNode;
  className?: string;
}

interface FormValues {
  isAcknowledged: boolean;
}

const Enroll = ({ children, className }: EnrollProps) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { handleSubmit, control, reset } = useForm<FormValues>({
    defaultValues: {
      isAcknowledged: false,
    },
    mode: "onChange",
  });

  const isAcknowledged = useWatch({
    control,
    name: "isAcknowledged",
  });

  const handleOpen = () => {
    setOpen(true);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);

    if (!isOpen) {
      reset();
    }
  };

  const onSubmit = async () => {
    setIsSubmitting(true);

    try {
      const baseUrl = process.env.NEXT_PUBLIC_ADMIN_PANEL_BASE_URL;

      if (!baseUrl) {
        throw new Error("Admin panel URL is not configured.");
      }

      await new Promise((resolve) => setTimeout(resolve, 800));

      window.location.assign(`${baseUrl}/register?from=home`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Redirection failed"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={handleOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOpen();
          }
        }}
        className={cn(
          "flex cursor-pointer items-center justify-center",
          className
        )}
      >
        {children}
      </div>

      <Modal
        open={open}
        onOpenChange={handleOpenChange}
        classes={{
          dialogContent: "sm:max-w-[650px]",
        }}
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col overflow-y-auto"
        >
          {/* Header */}
          <div>
            <h3 className="text-primary mb-4 text-4xl font-semibold">
              Review NDA before enrollment
            </h3>

            <p className="mb-6 text-xl font-medium text-slate-600">
              Read the full agreement. The accept action becomes available only
              after scrolling to the end and checking the acknowledgement.
            </p>
          </div>

          {/* Agreement */}
          <div className="max-h-[400px] w-full overflow-y-auto rounded-xl border border-slate-300 bg-slate-50 p-4">
            <div className="space-y-4">
              <h4 className="text-xl font-bold text-slate-800">
                Basic Training Agreement
              </h4>

              <p className="text-base text-slate-700">
                THIS IS A LEGAL AGREEMENT BETWEEN YOU (&quot;AGENT&quot; or
                &quot;YOU&quot;) AND TROPHY CLUB CONSULTING LLC
                (&quot;PRESENTER&quot;). By signing up for access to Basic
                Training for your employees, you are agreeing to be legally
                bound by all of the terms and conditions of this Enrollment
                Agreement (the &quot;Agreement&quot;).
              </p>

              <ol className="list-decimal space-y-4 pl-5">
                <li>
                  <h5 className="mb-3 font-semibold text-slate-900">
                    Agent Generally Agrees as follows:
                  </h5>

                  <ol className="list-[lower-alpha] space-y-4 pl-5">
                    <li>
                      The Training Materials (defined below) are the sole and
                      exclusive property of Presenter or its licensors, and
                      Agent will not allow the Training Materials or any portion
                      thereof, to be used except by its employees and only while
                      attending the training;
                    </li>

                    <li>
                      Agent shall not cause or allow its employees to copy or
                      cause to be copied or reproduced in any way, form, or
                      manner, electronic or otherwise, any of the Training
                      Materials or portion thereof;
                    </li>

                    <li>
                      Agent shall not cause or allow its employees to, directly
                      or indirectly, distribute, make available, publicly
                      display, publicly perform, create derivative works from
                      the Training Materials or portion thereof in any form or
                      manner, including online;
                    </li>
                    <li>
                      Neither Agent nor its employees are permitted to record
                      any of the provided training in any audio, video or other
                      formats. No photography, video taping, filming, audio
                      recording, or the like is permitted
                    </li>
                  </ol>
                </li>

                <li>
                  <span className="font-semibold text-slate-900">
                    Refunds/Cancellation.
                  </span>{" "}
                  There are no refunds. If the Organization deletes a Trainee by
                  11AM Eastern Time for any reason, a credit for a future Basic
                  Training session will be granted.
                </li>

                <li>
                  <span className="font-semibold text-slate-900">
                    Your Participation May be Recorded.
                  </span>{" "}
                  By participating in the training, you understand that portions
                  of the training may be recorded in video and audio and/or
                  captured in still and/or digital photographs. You hereby grant
                  Presenter and its affiliates and assigns the right and
                  permission to use in perpetuity your name, likeness,
                  photographic images, voice, and/or testimonial(s), in any
                  manner and in any media, throughout the world, at any time,
                  for the purpose of advertising and publicizing Presenter’s
                  products and services. Presenter owns all rights, title and
                  interest, including intellectual property rights, in any
                  audio, video, and/or photograph captured during the training.
                  Presenter reserves the right to use any such photographs and
                  video and/or audio recordings in any media, in perpetuity,
                  without any compensation or prior review unless specifically
                  prohibited by law, including without limitation on its website
                  and social media pages.
                </li>

                <li>
                  <span className="font-semibold text-slate-900">
                    Intellectual Property Rights.
                  </span>{" "}
                  You acknowledge that (i) anyone attending the training may
                  receive and/or have access to certain materials, information,
                  data, methodologies, and other creative output (collectively,
                  “Content”), and (ii) this Content is owned by Presenter, its
                  employees, licensors, representatives, business associates,
                  advertisers, directors, agents, consultants, mentors,
                  lecturers, and independent contractors participating in the
                  training (collectively referred to as “Contributors”). You
                  acknowledge that Presenter, and other Contributors own all
                  right, title and interest, including intellectual property
                  rights, in their respective Content under copyright and other
                  applicable laws and treaty provisions, and that except as
                  described in this Agreement, such rights are not licensed or
                  otherwise transferred by mere attendance or registration for
                  the training. You accept full responsibility and liability for
                  your use of any Content in violation of any such rights. You
                  also acknowledge that Presenter is not responsible for any
                  misuse of Content by third parties.
                </li>

                <li>
                  <span className="font-semibold text-slate-900">
                    Program Materials.
                  </span>{" "}
                  <span>
                    You also agree that all the materials displayed or
                    distributed during the training, or provided to you or which
                    you may have access to in conjunction with the training,
                    including without limitation: class handouts, teaching
                    materials, presentations, audio and video material or stills
                    from audiovisual material, graphics, slides, drawings,
                    pictures, characters, animations, text, and data, and all
                    intellectual property rights therein (such as copyrights and
                    trademarks) (collectively “the Training Materials”) are the
                    sole and exclusive property of Presenter or its
                    Contributors. The Training Materials shall remain at all
                    times the property of Presenter and/or of the Contributors.
                    You shall not cause or allow Your employees to copy,
                    distribute, make available, display, publish, perform or
                    create derivative works from (by electronic means or in any
                    other way) the Training Materials or Content without prior
                    written permission from an authorized agent of Presenter.
                    This includes forwarding or commercially exploiting the
                    Training Materials or content. Neither this Agreement nor
                    Presenter’s disclosure or display of the Training Materials
                    or Content shall be deemed, by implication or otherwise, to
                    grant you any licensed interest or property right in, under
                    or to any of the Training Materials or Content and any
                    intellectual property rights therein.
                  </span>
                </li>

                <li>
                  <h5 className="mb-3 font-semibold text-slate-900">
                    Waiver and Release From Liability.
                  </h5>

                  <ol className="list-[lower-alpha] space-y-4 pl-5">
                    <li>
                      You, on behalf of yourself and your assigns and heirs,
                      devisees and estate (collectively “Successors”), hereby
                      unconditionally and forever release, discharge and agree
                      to hold harmless Presenter, and its affiliates, assigns
                      and subsidiaries, along with each of its officers,
                      directors, employees, volunteers, agents and contractors
                      (collectively, “Released Parties”), from any and all
                      claims, judgments, costs, damages, losses, expenses and
                      liabilities (whether arising under a theory of contract,
                      warranty, tort, strict liability, product liability or any
                      other theory), relating to any claim you may now or
                      hereafter have with respect to any death, personal injury,
                      property damages, pecuniary loss or other loss, damage
                      cost or expense (collectively “Harm”) that may be suffered
                      by you or any third party as a result of, or in connection
                      with, the training, or any portion thereof, even if such
                      harm is caused in part by the recklessness, negligence, or
                      fault of one or more Released Parties.
                    </li>

                    <li className="font-semibold">
                      You specifically understand and agree that this Release
                      will prevent you and your successor(s) from bringing a
                      lawsuit, claim or other action against any Released Party
                      and from recovering any money damages or other legal
                      relief from any Released Party in connection with any of
                      the claims released above
                    </li>
                  </ol>
                </li>

                <li>
                  <span className="font-semibold text-slate-900">
                    Indemnification.
                  </span>{" "}
                  <span>
                    You agree to, and shall, indemnify, defend, and hold
                    harmless Presenter and its parent company, affiliates,
                    subsidiaries, divisions, officers, directors, employees,
                    representatives, agents, contractors, assigns and
                    successors-in-interest (collectively “Releasees”) from and
                    against any claim, demand, damage, cost or other expense
                    (including reasonable attorney fees) asserted by any third
                    party due to, arising out of, or in any way related to, any
                    violation by You of this Agreement.
                  </span>
                </li>
              </ol>

              <Controller
                name="isAcknowledged"
                control={control}
                render={({ field }) => (
                  <div className="mt-6 flex items-center gap-2 border-t pt-4">
                    <Checkbox
                      id="acknowledgement"
                      checked={field.value}
                      onCheckedChange={(checked) =>
                        field.onChange(checked === true)
                      }
                      className="cursor-pointer data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white"
                    />

                    <label
                      htmlFor="acknowledgement"
                      className={cn(
                        "cursor-pointer text-lg font-semibold text-slate-800"
                      )}
                    >
                      I have read the full NDA and agree to continue.
                    </label>
                  </div>
                )}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-4 grid w-full grid-cols-2 gap-4">
            <Button
              type="button"
              variant="outline"
              size="xl"
              className="shrink-0 grow rounded-full"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              size="xl"
              className="shrink-0 grow rounded-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600"
              disabled={!isAcknowledged || isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Loader className="animate-spin" /> Redirecting...
                </span>
              ) : (
                "Continue"
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default Enroll;
